import DOMPurify from 'isomorphic-dompurify';
import { AlertTriangle, ArrowLeft, BellRing, ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { PageHeader } from '../components/AppShell';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Spinner } from '../components/ui/spinner';
import { useToast } from '../components/ui/toast';
import { PreviousBroadcastNotice } from '../components/PreviousBroadcastNotice';
import { useLastBroadcast, useTriggerBreakingNews } from '../lib/api/breaking';
import { usePost } from '../lib/api/posts';
import { useAuthStore } from '../lib/auth/store';
import { fmtDateTime, fmtLagos } from '../lib/date';
import { useLocaleStore } from '../lib/locales';
import { plainText } from '../lib/utils';

export default function PostDetail(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const type: 'post' | 'page' = location.pathname.startsWith('/pages') ? 'page' : 'post';
  const locale = useLocaleStore((s) => s.current);
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const { showToast } = useToast();

  const detail = usePost(slug ?? null, locale, type);
  const breakingMut = useTriggerBreakingNews();
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Only fire the lookup once the dialog actually opens — keeps the page
  // load light and ensures the data is fresh when the editor needs it.
  const lastBroadcast = useLastBroadcast(slug ?? null, locale, type, {
    enabled: confirmOpen,
  });

  // Re-sanitise on every change. WordPress content can contain script-injection
  // payloads if it ever bypasses ingest sanitisation; this is the last line of
  // defence before the admin's browser executes anything.
  const sanitisedContent = useMemo(
    () => (detail.data?.content ? DOMPurify.sanitize(detail.data.content) : ''),
    [detail.data?.content],
  );

  async function broadcast(): Promise<void> {
    if (!slug) return;
    try {
      const resp = await breakingMut.mutateAsync({ type, slug, locale });
      setConfirmOpen(false);
      showToast({
        title: 'Breaking-news push queued',
        description: `Will fire ${fmtLagos(resp.scheduledAt, 'PP p')}.`,
        variant: 'success',
      });
    } catch (err) {
      showToast({
        title: 'Could not queue push',
        description: (err as Error).message,
        variant: 'danger',
      });
    }
  }

  return (
    <>
      <Topbar
        title={type === 'post' ? 'Post' : 'Page'}
        subtitle={slug}
        actions={
          isAdmin && type === 'post' ? (
            <Button onClick={() => setConfirmOpen(true)} disabled={breakingMut.isPending}>
              <BellRing className="h-4 w-4" />
              {breakingMut.isPending ? 'Queuing…' : 'Broadcast as breaking news'}
            </Button>
          ) : undefined
        }
      />
      <div className="px-6 py-3">
        <Link
          to={`/${type}s`}
          className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" /> Back to {type}s
        </Link>
      </div>

      {detail.isLoading ? (
        <div className="px-6">
          <Spinner label="Loading…" />
        </div>
      ) : detail.data ? (
        <>
          <PageHeader
            title={plainText(detail.data.title)}
            subtitle={`Published ${fmtDateTime(detail.data.publishedAt)} · ${detail.data.author?.name ?? 'Unknown author'}`}
            actions={
              <a
                href={detail.data.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
              >
                View on von.gov.ng <ExternalLink className="h-3.5 w-3.5" />
              </a>
            }
          />
          <div className="grid grid-cols-1 gap-6 px-6 pb-10 lg:grid-cols-[1fr_320px]">
            <article className="space-y-4">
              {/* Article headline — `PageHeader` deliberately doesn't render
               *  its `title` prop (would duplicate the global Topbar's chrome
               *  title), so the actual news title gets rendered here as an
               *  editorial <h1> instead. */}
              <h1 className="font-heading text-[28px] font-semibold leading-[1.2] tracking-tight text-ink lg:text-[32px]">
                {plainText(detail.data.title)}
              </h1>
              {detail.data.featuredImage?.url ? (
                <img
                  src={detail.data.featuredImage.url}
                  alt={detail.data.featuredImage.alt ?? ''}
                  className="aspect-[16/9] w-full rounded-lg object-cover shadow-card"
                />
              ) : null}
              <div
                className="post-content w-full rounded-lg border border-line bg-surface p-5 shadow-card"
                // biome-ignore lint/security/noDangerouslySetInnerHtml: content is sanitised via DOMPurify above before injection.
                dangerouslySetInnerHTML={{ __html: sanitisedContent }}
              />
            </article>

            <aside className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Metadata</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <Field label="Slug" value={detail.data.slug} />
                  <Field label="Type" value={detail.data.type} />
                  <Field label="Words" value={String(detail.data.meta.wordCount)} />
                  <Field label="Reading" value={`${detail.data.meta.readingTimeMinutes} min`} />
                  <Field label="Modified" value={fmtDateTime(detail.data.modifiedAt)} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {!Array.isArray(detail.data.categories) ||
                    detail.data.categories.length === 0 ? (
                      <p className="text-sm text-ink-muted">None</p>
                    ) : (
                      detail.data.categories.map((c) => (
                        <Badge key={c.slug} variant="outline">
                          {c.name}
                        </Badge>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Tags</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {!Array.isArray(detail.data.tags) || detail.data.tags.length === 0 ? (
                      <p className="text-sm text-ink-muted">None</p>
                    ) : (
                      detail.data.tags.map((t) => (
                        <Badge key={t.slug} variant="outline">
                          #{t.name}
                        </Badge>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              {detail.data.reactionCounts || typeof detail.data.bookmarkCount === 'number' ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Engagement</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="grid grid-cols-3 gap-2 text-center">
                      <EngagementStat
                        label="Likes"
                        value={detail.data.reactionCounts?.likes ?? 0}
                      />
                      <EngagementStat
                        label="Dislikes"
                        value={detail.data.reactionCounts?.dislikes ?? 0}
                      />
                      <EngagementStat
                        label="Saves"
                        value={detail.data.bookmarkCount ?? 0}
                      />
                    </ul>
                  </CardContent>
                </Card>
              ) : null}
            </aside>
          </div>
        </>
      ) : (
        <div className="px-6 pb-10 text-sm text-ink-muted">Item not found.</div>
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => !breakingMut.isPending && setConfirmOpen(o)}>
        <DialogContent size="md">
          <DialogHeader>
            <p className="eyebrow">
              <BellRing className="h-3 w-3" /> Breaking-news push
            </p>
            <DialogTitle>Broadcast this story?</DialogTitle>
            <DialogDescription>
              Confirm before we queue a push to every subscriber on the {locale.toUpperCase()} channel.
            </DialogDescription>
          </DialogHeader>

          <div className="-mx-2 flex-1 space-y-3 overflow-y-auto px-2">
            {detail.data ? (
              <div className="rounded-md border border-line bg-surface-tinted/40 px-3 py-2.5">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
                  Headline
                </p>
                <p className="mt-0.5 line-clamp-3 text-[13.5px] font-semibold leading-snug text-ink">
                  {plainText(detail.data.title)}
                </p>
              </div>
            ) : null}

            {lastBroadcast.data ? (
              <PreviousBroadcastNotice info={lastBroadcast.data} />
            ) : null}

            <ul className="space-y-1.5 text-[12.5px] leading-snug text-ink-muted">
              <li className="flex gap-2">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand/60" />
                <span>
                  Reaches <strong className="text-ink">every device subscribed to <code className="font-mono">breaking-news-{locale}</code></strong> — anonymous installs included, not just signed-in users.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand/60" />
                <span>
                  The push is held for ~15 minutes before delivery. You can cancel it from the Breaking News → History tab during that window.
                </span>
              </li>
              <li className="flex gap-2">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
                <span>
                  After it fires, you cannot recall the notification from devices.
                </span>
              </li>
            </ul>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={breakingMut.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void broadcast()}
              disabled={breakingMut.isPending}
            >
              <BellRing className="h-3.5 w-3.5" />
              {breakingMut.isPending ? 'Queuing…' : 'Yes, broadcast'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="grid grid-cols-[100px_1fr] items-baseline gap-2">
      <span className="text-xs uppercase tracking-wide text-ink-subtle">{label}</span>
      <span className="font-mono text-xs text-ink">{value}</span>
    </div>
  );
}

function EngagementStat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <li className="rounded-md border border-line bg-surface-tinted/40 px-2 py-2">
      <p className="font-heading text-[18px] font-semibold tabular-nums tracking-tight text-ink">
        {value.toLocaleString()}
      </p>
      <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-subtle">
        {label}
      </p>
    </li>
  );
}
